/**
 * FundDetails Component
 * Displays fund/balance information from the broker account
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
} from "@mui/material";
import { services } from "../../../../services/api";
import { Icon } from "../../../../components/ds";
import IciciFundDetails from "./BrokerSpecific/Icici/IciciFundDetails";

const FundDetails = ({ account }) => {
  // Component state
  const [loading, setLoading] = useState(true);
  const [funds, setFunds] = useState(null);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load fund details
  const loadFundDetails = useCallback(async () => {
    if (!account?.account_id) {
      setError("No account ID available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      console.log("Fetching fund details for account:", account.account_id);
      const response = await services.brokerAccounts.getFunds(account.account_id);

      if (response.data && response.data.status === "success") {
        setFunds(response.data.data);
        setLastUpdated(new Date());
        console.log("Fund details loaded:", response.data.data);
      } else {
        const errorMsg = response.data?.error || response.data?.message || "Failed to load fund details";
        setError(errorMsg);
        console.error("Error loading fund details:", errorMsg);
      }
    } catch (err) {
      console.error("Error in loadFundDetails:", err);
      setError(`Failed to load fund details: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }, [account?.account_id]);

  useEffect(() => {
    if (account?.account_id) {
      loadFundDetails();
    }
  }, [account?.account_id, loadFundDetails]);

  // Format currency
  const formatCurrency = (amount, currency = "INR") => {
    if (amount === null || amount === undefined) return "N/A";
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return "N/A";
    
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  // Format large numbers
  const formatNumber = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return "N/A";
    
    return new Intl.NumberFormat('en-IN').format(numAmount);
  };

  if (loading) {
    return (
      <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
        <div className="p-6 flex items-center justify-center min-h-[120px]">
          <svg className="animate-spin h-6 w-6 text-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          <span className="ml-3 text-fg-2">Loading fund details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
        <div className="p-6">
          <Alert 
            severity="error" 
            icon={<Icon name="error" />}
            className="mb-4"
          >
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>
          
          <Box className="text-center">
            <Typography variant="body2" className="text-fg-2 mb-3">
              Unable to load fund details. Please check your connection and try again.
            </Typography>
          </Box>
        </div>
      </div>
    );
  }

  // Check if broker is ICICI (broker_id = 1) for broker-specific component
  if (account?.broker_id === 1 && funds) {
    return (
      <IciciFundDetails 
        account={account}
        funds={funds}
        lastUpdated={lastUpdated}
        onRefresh={loadFundDetails}
        formatCurrency={formatCurrency}
        formatNumber={formatNumber}
      />
    );
  }

  // Generic fund details display for other brokers
  return (
    <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)]">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Icon name="bank" className="text-accent shrink-0" size={28} />
            <div className="min-w-0">
              <Typography variant="h6" className="text-fg-1 font-medium">
                Fund Details
              </Typography>
              <Typography variant="caption" className="text-fg-2">
                Available balance and allocations
              </Typography>
            </div>
          </div>
          {lastUpdated && (
            <Typography variant="caption" className="text-fg-2 shrink-0">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </div>

        <Divider className="border-[var(--divider)] mb-6" />

        {/* Fund Information */}
        {funds ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" icon={<Icon name="info" />}>
                Fund details are available. Displaying raw broker data.
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" className="mb-2">
                    Broker Fund Data
                  </Typography>
                  <pre className="bg-bg-3 text-fg-2 p-3 rounded text-xs overflow-x-auto max-w-full">
                    {JSON.stringify(funds, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <div className="text-center py-8">
            <Typography variant="body1" className="text-fg-2">
              No fund data available
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundDetails;
